import type { WebsiteBlock } from '../../types'
import { useBranding } from '../../contexts/BrandingContext'
import { useI18n } from '../../i18n'
import * as Icons from 'lucide-react'

interface FeaturesProps {
  title: string
  titleEn?: string
  columns: 2 | 3 | 4
  items: Array<{
    icon: string
    title: string
    titleEn?: string
    description: string
    descriptionEn?: string
  }>
}

function getIcon(name: string) {
  const Icon = (Icons as any)[name] || Icons.Zap
  return Icon
}

export default function FeaturesBlock({ block }: { block: WebsiteBlock }) {
  const { branding } = useBranding()
  const { lang } = useI18n()
  const p = block.props as FeaturesProps

  const title = lang === 'en' && p.titleEn ? p.titleEn : p.title
  const hasContent = title?.trim() || (p.items && p.items.length > 0)
  if (!hasContent) return null

  const cols = p.columns || 3
  const gridCols = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'

  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {title?.trim() && (
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 tracking-tight"
            style={{ color: branding.secondaryColor }}>
            {title}
          </h2>
        )}
        <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
          {(p.items || []).map((item, i) => {
            const Icon = getIcon(item.icon)
            return (
              <div key={i} className="p-6 rounded-2xl bg-white border border-black/[0.04] hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${branding.primaryColor}12` }}>
                  <Icon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                </div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: branding.secondaryColor }}>
                  {lang === 'en' && item.titleEn ? item.titleEn : item.title}
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed">
                  {lang === 'en' && item.descriptionEn ? item.descriptionEn : item.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
